import { useCallback } from 'react';

import { useConnections } from '%context/Connections';
import { PostsFilter } from '%services/postsFilter/type';
import { useNewPostInFeedSubscription } from './__generated__/newPost.subscription.generated';
import { useGetCurrentUserPostsQuery } from './__generated__/userPosts.generated';

const PAGE_SIZE = 5;

export const useUserPosts = ({
	filters,
}: {
	filters?: { loading: boolean; value?: PostsFilter };
}) => {
	const { careRecipients } = useConnections();
	const { loading, error, data, updateQuery, refetch, fetchMore } = useGetCurrentUserPostsQuery({
		variables: {
			first: PAGE_SIZE,
			filters: filters?.value
				? {
						careRecipients:
							filters.value.careRecipients === 'everyone'
								? undefined
								: filters.value.selectedCareRecipients,
						userTags: filters.value.userTags,
						...(filters.value.publishedInLastDays !== 'custom'
							? {
									publishedInLastDays:
										filters.value.publishedInLastDays === 'all'
											? undefined
											: Number(filters.value.publishedInLastDays),
								}
							: {
									publishedInCustomPeriod: {
										from: filters.value.publishedInCustomPeriod.startDate,
										to: filters.value.publishedInCustomPeriod.endDate,
									},
								}),
					}
				: undefined,
		},
		notifyOnNetworkStatusChange: true,
		skip: filters?.loading,
	});
	useNewPostInFeedSubscription({
		variables: {
			userUuid: careRecipients.map((protege) => protege.uuid),
		},
		skip: !careRecipients.length,
		onData: ({ data: { data } }) => {
			if (!data) {
				return;
			}

			return updateQuery((query) => {
				const isOwnPost = data.newPostInCareReceiverFeed?.node?.author?.id === query.currentUser.id;
				const isCreatingInProgress = query.currentUser.posts?.edges.some((edge) =>
					edge.node.id.includes('temp-id-'),
				);
				if (isOwnPost && isCreatingInProgress) {
					return query;
				}

				const isAlreadyInFeed = query.currentUser.posts?.edges.some(
					({ node }) => node.id === data.newPostInCareReceiverFeed?.node?.id,
				);
				if (isAlreadyInFeed) {
					return query;
				}

				return Object.assign({}, query, {
					...query,
					currentUser: {
						...query.currentUser,
						posts: {
							...query.currentUser.posts,
							edges: [
								{
									cursor: data.newPostInCareReceiverFeed?.cursor,
									node: data.newPostInCareReceiverFeed?.node,
								},
								...(query.currentUser.posts?.edges || []),
							],
						},
					},
				});
			});
		},
	});
	const postConnection = data?.currentUser.posts;
	const posts = postConnection?.edges.map(({ node }) => node) || [];
	const { hasNextPage, endCursor } = postConnection?.pageInfo || {};
	const hasCreatedPosts = data?.currentUser.hasCreatedPosts || false;

	const loadMore = useCallback(() => {
		if (!hasNextPage) {
			return;
		}

		return fetchMore({
			variables: {
				after: endCursor,
			},
		});
	}, [hasNextPage, endCursor, fetchMore]);

	return { loading, error, posts, refetch, loadMore, hasNextPage, hasCreatedPosts };
};
